import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from './client';
import { companionsKeys } from './companions';
import type { Outfit } from '../pet3d/types';

/** Mirrors the server's WardrobeDtos.EquippedSlotDto after JSON transport (Map<Integer,String>
 *  keys always serialise as strings): dye channel "1" -> owned dye's item code. `projected` is
 *  `true` only for an entry synthesised by WardrobeService#buildState from a visible transmog
 *  projection -- never owned, never dyeable, never sent back by the PUT (`stripProjected`). */
export type EquippedSlot = { itemCode: string; dye: Record<string, string>; projected?: boolean };

/** Mirrors the server's WardrobeDtos.SlotDto. */
export type SlotDto = { code: string; zOrder: number; minStage: string };

/** Mirrors the server's WardrobeDtos.DyeDto. */
export type DyeDto = { code: string; name: string; hex: string };

/** The wardrobe's equipped map as the 3D pet wears it: dye channel "1" resolved to its hex
 *  (`null` undyed, or a dye this keeper no longer owns); projected slots kept -- they are on the
 *  pet even though they are not wardrobe pieces. */
export function toOutfit(equipped: Record<string, EquippedSlot>, dyes: DyeDto[]): Outfit {
  const hex = new Map(dyes.map((d) => [d.code, d.hex] as const));
  const out: Outfit = {};
  for (const [slot, e] of Object.entries(equipped)) {
    const code = e.dye['1'];
    out[slot] = { itemCode: e.itemCode, dyeHex: code ? hex.get(code) ?? null : null };
  }
  return out;
}

export type ItemDto = {
  code: string;
  name: string;
  slot: string;
  rarity: string;
  dyeChannels: number;
  description: string;
};

export type WardrobeState = {
  companionId: number;
  speciesCode: string;
  stage: string;
  equipped: Record<string, EquippedSlot>;
  ownedItems: ItemDto[];
  ownedDyes: DyeDto[];
  slots: SlotDto[];
};

export const wardrobeKeys = {
  detail: (companionId: number) => ['wardrobe', companionId] as const,
};

export const getWardrobe = (companionId: number) =>
  apiFetch<WardrobeState>(`/wardrobe/${companionId}`);

/**
 * Free-transform final-review fix: drops every `projected: true` entry before a slot map goes
 * on the wire (the outfit PUT here, the Look save in api/looks.ts). A projected entry is a
 * transmog projection buildState merged into GET's equipped map at render time — its cos_eq_*
 * code is never in the player's inventory, so echoing it back (which the draft otherwise does,
 * since it seeds from that same merged map) made the server 400 `not_owned` on EVERY wardrobe
 * edit and Look save while any projection was visible. Omitting the slot is safe: the server
 * treats a projected slot with no submitted entry as "keep the real row hidden underneath it"
 * (WardrobeService.resolveProjectionEchoes — the row GET never showed us, so we could not
 * possibly resubmit it ourselves), and it independently resolves any echo that still arrives.
 */
export function stripProjected(slots: Record<string, EquippedSlot>): Record<string, EquippedSlot> {
  const real: Record<string, EquippedSlot> = {};
  for (const [slot, entry] of Object.entries(slots)) {
    if (!entry.projected) real[slot] = entry;
  }
  return real;
}

export const putWardrobe = (companionId: number, slots: Record<string, EquippedSlot>) =>
  apiFetch<WardrobeState>(`/wardrobe/${companionId}`, {
    method: 'PUT',
    body: JSON.stringify({ slots: stripProjected(slots) }),
  });

export function useWardrobe(companionId: number | undefined) {
  return useQuery({
    queryKey: wardrobeKeys.detail(companionId ?? -1),
    queryFn: () => getWardrobe(companionId as number),
    enabled: companionId != null,
    // QA 2026-09-23 onboard F16: a 404 is an answer (not your companion, or no such id), not a
    // blip — retrying it three times with backoff kept the screen on its skeleton for ~7 s
    // before the not-found card could render. Everything else keeps the default retry.
    retry: (count, err) => !(err instanceof ApiClientError && err.status === 404) && count < 3,
  });
}

/** Shared `mutate()`/`isMutating()` key for a companion's outfit commits — see the mutationKey
 *  and scope note on `usePutOutfit` below. Exported so callers (`WardrobeScreen`) can ask
 *  `queryClient.isMutating({ mutationKey: putOutfitKey(companionId) })` for a live "is another
 *  commit for this companion still in flight" signal without duplicating the key shape. */
export const putOutfitKey = (companionId: number) => ['putOutfit', companionId] as const;

/**
 * Optimistic outfit change (§11.3): applies the draft equipped map to the cached wardrobe
 * immediately, then rolls back to the pre-mutation snapshot if the server rejects it (e.g.
 * a 400 `not_owned` — item removed from inventory by another session, or never owned).
 * `onSettled` always refetches (and — critically — *awaits* that refetch, not fire-and-forget,
 * so anyone awaiting `mutateAsync` only resumes once the cache holds fresh server truth) so the
 * cache converges on the server's truth either way.
 *
 * `scope` (review finding C2, Task 14 fix round): two equips tapped in quick succession used to
 * fire two genuinely concurrent PUTs. If the first later failed while the second was still in
 * flight, the first's rollback could stomp the second's still-optimistic cache write, and — since
 * network responses aren't guaranteed to arrive in send order — the *second* (the player's actual
 * latest intent) could even lose a "who wins" race to the *first* (stale) response. Scoping both
 * mutations to the same `id` makes react-query queue them: a later commit's actual network call
 * never starts until the earlier one has fully settled (rollback + awaited refetch included), so
 * completion order is always send order. See `WardrobeScreen.commitDraft` for the other half of
 * this fix — re-syncing the local draft from the (by-then-trustworthy) fresh cache after settle.
 */
export function usePutOutfit(companionId: number) {
  const queryClient = useQueryClient();
  const key = wardrobeKeys.detail(companionId);
  return useMutation({
    mutationKey: putOutfitKey(companionId),
    scope: { id: `wardrobe-outfit-${companionId}` },
    mutationFn: (slots: Record<string, EquippedSlot>) => putWardrobe(companionId, slots),
    onMutate: async (slots) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<WardrobeState>(key);
      if (previous) queryClient.setQueryData<WardrobeState>(key, { ...previous, equipped: slots });
      return { previous };
    },
    onError: (_err, _slots, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    // The companions list carries the worn outfit too (the Habitat's 3D pet wears it).
    onSettled: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: key }),
      queryClient.invalidateQueries({ queryKey: companionsKeys.all }),
    ]),
  });
}
